/* eslint-disable no-empty */
import {
  AnalysisIssue,
  AnalysisIssuesOverview,
  ResponseAnalysisAcknowledgeIssue,
  ResponseAnalysisGetIssuesMe,
  ResponseAnalysisHideIssue,
  ResponseAnalysisUnhideIssue,
  ResponseBasketballGetAnalyze,
} from '@hefe/models';
import { createApi } from '@reduxjs/toolkit/query/react';
import API from '../../../api/api';

interface IssueListQueryArgs {
  page?: number;
  filter?: 'open' | 'hidden';
  include_hidden?: boolean;
  per_page?: number;
  only_open?: boolean;
}

interface TeamIssueQueryArgs extends IssueListQueryArgs {
  teamId: number;
}

interface ClubIssueQueryArgs extends IssueListQueryArgs {
  clubId: number;
}

/**
 * Generates a stable cache key for an issue list.
 *
 * The page is deliberately excluded so that all pages of the same
 * list share one cache entry.
 */
function getIssueListCacheKey(
  endpointName: string,
  args: IssueListQueryArgs,
  entityId?: number
): string {
  const {
    include_hidden = false,
    per_page = 50,
    only_open = false,
    filter,
  } = args;

  return JSON.stringify({
    endpointName,
    entityId,
    include_hidden: filter ? undefined : include_hidden,
    per_page,
    only_open,
    filter,
  });
}

/**
 * Appends newly loaded pages to the existing issue list.
 *
 * Page 1 resets the list. Existing issues are replaced by their
 * newer versions instead of being added twice.
 */
function mergeIssuePages(
  currentCache: ResponseAnalysisGetIssuesMe,
  newItems: ResponseAnalysisGetIssuesMe
): ResponseAnalysisGetIssuesMe {
  if (newItems.meta.current_page === 1) {
    return newItems;
  }

  const issues = [...(currentCache.data ?? [])];
  const existingIds = new Map(issues.map((issue, index) => [issue.id, index]));

  for (const issue of newItems.data ?? []) {
    const existingIndex = existingIds.get(issue.id);

    if (existingIndex !== undefined) {
      issues[existingIndex] = issue;
    } else {
      existingIds.set(issue.id, issues.length);
      issues.push(issue);
    }
  }

  return {
    ...currentCache,
    ...newItems,
    data: issues,
  };
}

/**
 * Determines whether the query arguments require a new request.
 */
function shouldRefetchIssues<T extends IssueListQueryArgs>(
  currentArg?: T,
  previousArg?: T
): boolean {
  if (!currentArg || !previousArg) {
    return false;
  }

  return (
    (currentArg.page ?? 1) !== (previousArg.page ?? 1) ||
    (currentArg.include_hidden ?? false) !==
      (previousArg.include_hidden ?? false) ||
    (currentArg.per_page ?? 50) !== (previousArg.per_page ?? 50) ||
    (currentArg.only_open ?? false) !== (previousArg.only_open ?? false) ||
    currentArg.filter !== previousArg.filter
  );
}

export const userApi = createApi({
  reducerPath: 'apiIssues',
  baseQuery: API.baseQuery,

  tagTypes: ['AnalysisIssueOverview', 'AnalysisIssueList'],

  endpoints: (builder) => ({
    fetchClubAnalyze: builder.query<
      ResponseBasketballGetAnalyze,
      { from: string }
    >({
      query: (request) => ({
        url: `/club/basketball/analyze?from=${request.from}`,
        method: 'GET',
      }),
    }),

    /**
     * MY ISSUES
     */
    fetchMyIssues: builder.query<
      ResponseAnalysisGetIssuesMe,
      IssueListQueryArgs
    >({
      query: ({
        page = 1,
        include_hidden = false,
        per_page = 50,
        only_open = false,
        filter,
      }) => ({
        url: '/analysis/issues/me',
        method: 'GET',
        params: {
          page,
          per_page,
          only_open,
          ...(filter ? { filter } : { include_hidden }),
        },
      }),

      providesTags: [{ type: 'AnalysisIssueList', id: 'me' }],

      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        getIssueListCacheKey(endpointName, queryArgs),

      merge: mergeIssuePages,

      forceRefetch: ({ currentArg, previousArg }) =>
        shouldRefetchIssues(currentArg, previousArg),
    }),

    /**
     * ISSUES OVERVIEW
     */
    fetchIssuesOverview: builder.query<AnalysisIssuesOverview, void>({
      query: () => ({
        url: '/analysis/issues/overview',
        method: 'GET',
      }),

      providesTags: ['AnalysisIssueOverview'],
    }),

    /**
     * TEAM ISSUES
     */
    fetchTeamIssues: builder.query<
      ResponseAnalysisGetIssuesMe,
      TeamIssueQueryArgs
    >({
      query: ({
        teamId,
        page = 1,
        include_hidden = false,
        per_page = 50,
        only_open = false,
        filter,
      }) => ({
        url: `/basketball/teams/${teamId}/analysis/issues`,
        method: 'GET',
        params: {
          page,
          per_page,
          only_open,
          ...(filter ? { filter } : { include_hidden }),
        },
      }),

      providesTags: (_result, _error, { teamId }) => [
        { type: 'AnalysisIssueList', id: `team-${teamId}` },
      ],

      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        getIssueListCacheKey(endpointName, queryArgs, queryArgs.teamId),

      merge: mergeIssuePages,

      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.teamId !== previousArg?.teamId ||
        shouldRefetchIssues(currentArg, previousArg),
    }),

    /**
     * CLUB ISSUES
     */
    fetchClubIssues: builder.query<
      ResponseAnalysisGetIssuesMe,
      ClubIssueQueryArgs
    >({
      query: ({
        clubId,
        page = 1,
        include_hidden = false,
        per_page = 50,
        only_open = false,
        filter,
      }) => ({
        url: `/clubs/${clubId}/analysis/issues`,
        method: 'GET',
        params: {
          page,
          per_page,
          only_open,
          ...(filter ? { filter } : { include_hidden }),
        },
      }),

      providesTags: (_result, _error, { clubId }) => [
        { type: 'AnalysisIssueList', id: `club-${clubId}` },
      ],

      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        getIssueListCacheKey(endpointName, queryArgs, queryArgs.clubId),

      merge: mergeIssuePages,

      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.clubId !== previousArg?.clubId ||
        shouldRefetchIssues(currentArg, previousArg),
    }),

    /**
     * ACKNOWLEDGE ISSUE
     */
    acknowledgeIssue: builder.mutation<
      ResponseAnalysisAcknowledgeIssue,
      number
    >({
      query: (issueId) => ({
        url: `/analysis/issues/${issueId}/acknowledge`,
        method: 'POST',
      }),

      invalidatesTags: ['AnalysisIssueOverview', 'AnalysisIssueList'],

      async onQueryStarted(issueId, { dispatch, getState, queryFulfilled }) {
        await patchMyIssuesCache(
          issueId,
          { acknowledged: true },
          dispatch,
          getState,
          queryFulfilled
        );
      },
    }),

    /**
     * HIDE ISSUE
     */
    hideIssue: builder.mutation<ResponseAnalysisHideIssue, number>({
      query: (issueId) => ({
        url: `/analysis/issues/${issueId}/hide`,
        method: 'POST',
      }),

      invalidatesTags: ['AnalysisIssueOverview', 'AnalysisIssueList'],

      async onQueryStarted(issueId, { dispatch, getState, queryFulfilled }) {
        await patchMyIssuesCache(
          issueId,
          { hidden: true },
          dispatch,
          getState,
          queryFulfilled
        );
      },
    }),

    /**
     * UNHIDE ISSUE
     */
    unhideIssue: builder.mutation<ResponseAnalysisUnhideIssue, number>({
      query: (issueId) => ({
        url: `/analysis/issues/${issueId}/hide`,
        method: 'DELETE',
      }),

      invalidatesTags: ['AnalysisIssueOverview', 'AnalysisIssueList'],

      async onQueryStarted(issueId, { dispatch, getState, queryFulfilled }) {
        await patchMyIssuesCache(
          issueId,
          { hidden: false },
          dispatch,
          getState,
          queryFulfilled
        );
      },
    }),
  }),
});

/**
 * Optimistically updates cached issues for the current user.
 */
async function patchMyIssuesCache(
  issueId: number,
  changes: Partial<Pick<AnalysisIssue, 'acknowledged' | 'hidden'>>,
  dispatch: any,
  getState: () => any,
  queryFulfilled: Promise<{ data: AnalysisIssue }>
) {
  const cachedArgs = userApi.util.selectCachedArgsForQuery(
    getState(),
    'fetchMyIssues'
  );

  const patches = cachedArgs.map((args) =>
    dispatch(
      userApi.util.updateQueryData('fetchMyIssues', args, (draft) => {
        const index = draft.data?.findIndex(
          (item: AnalysisIssue) => item.id === issueId
        );

        if (index === undefined || index === -1) {
          return;
        }

        const merged = {
          ...draft.data[index],
          ...changes,
        } as AnalysisIssue;

        const shouldRemove =
          (merged.hidden && args.filter === 'open') ||
          (!merged.hidden && args.filter === 'hidden') ||
          (merged.hidden && !args.filter && !args.include_hidden);

        if (shouldRemove) {
          draft.data.splice(index, 1);
        } else {
          draft.data[index] = merged;
        }
      })
    )
  );

  try {
    const { data: updatedIssue } = await queryFulfilled;

    if (updatedIssue) {
      cachedArgs.forEach((args) => {
        dispatch(
          userApi.util.updateQueryData('fetchMyIssues', args, (draft) => {
            const index = draft.data?.findIndex(
              (item: AnalysisIssue) => item.id === issueId
            );

            if (index === undefined || index === -1) {
              return;
            }

            draft.data[index] = {
              ...draft.data[index],
              ...updatedIssue,
            };
          })
        );
      });
    }
  } catch (error: unknown) {
    patches.forEach((patch) => patch.undo());
  }
}

// Export hooks for usage in functional components
export const {
  useLazyFetchClubAnalyzeQuery,
  useFetchMyIssuesQuery,
  useLazyFetchMyIssuesQuery,
  useFetchIssuesOverviewQuery,
  useLazyFetchIssuesOverviewQuery,
  useFetchTeamIssuesQuery,
  useLazyFetchTeamIssuesQuery,
  useFetchClubIssuesQuery,
  useLazyFetchClubIssuesQuery,
  useAcknowledgeIssueMutation,
  useHideIssueMutation,
  useUnhideIssueMutation,
  reducerPath,
  reducer,
  middleware,
} = userApi;
